import {
  Box,
  Chip,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
  Backdrop,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  AddAttendanceCodeAction,
  DeleteAttendanceCodeAction,
  GetAttendanceCodeAction,
} from "../../../../../store/Leave/AttendanceBulkNotationCode/Action";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";

const AttendanceCustomCodeGrid = () => {
  const dispatch = useDispatch();

  const { atdCodeLoading, attendanceCodeData } = useSelector(
    (state) => state?.LeaveReducer.AttendanceReducer
  );
  const [rows, setRows] = useState([]);

  useEffect(() => {
    dispatch(GetAttendanceCodeAction());
  }, [dispatch]);

  // Structure data for chips
  useEffect(() => {
    if (attendanceCodeData?.length > 0) {
    const grouped = [];
    const seen = new Map();

    attendanceCodeData.forEach((item) => {
      const key = item.meepl_label;

      if (!seen.has(key)) {
        const newEntry = {
          attendance_bulk_notation_mapping_pk: item.attendance_bulk_notation_mapping_pk,
          meepl_label: key,
          user_code: [item],
        };
        grouped.push(newEntry);
        seen.set(key, newEntry); 
      } else {
        const existing = seen.get(key);
        const alreadyExists = existing?.user_code.some(
          (uc) => uc.attendance_bulk_notation_mapping_pk === item.attendance_bulk_notation_mapping_pk
        );

        if (!alreadyExists) {
          existing.user_code.push(item);
        }
      }
    });
      const structured = grouped?.sort((a, b) =>
        a?.meepl_label?.localeCompare(b?.meepl_label, undefined, { sensitivity: "base" })
      );

     setRows(structured);
    }
  }, [attendanceCodeData]);

  const handleAdd = (row, newCode) => {
    if (!newCode.trim()) return;
    dispatch(
      AddAttendanceCodeAction({
        meepl_label: row?.meepl_label,
        company_label: newCode,
      })
    );
  };

  // Handle Delete
  const handleDelete = (chip) => {
    dispatch(
      DeleteAttendanceCodeAction({
        attendance_bulk_notation_mapping_pk:
          chip?.attendance_bulk_notation_mapping_pk,
      })
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        whiteSpace: "nowrap", 
        backgroundColor: "#f8f5ff",
        py: 2,
        px: 4,
        borderRadius: 2,
        position: "relative",
      }}
    >
      {/* Header */}
      <Box
        display="grid"
        sx={{ mb: 2, fontWeight: 700, fontSize: 14, pl: 1,
           gridTemplateColumns: {
              xs: "150px 1fr 100px 80px",
              sm: "200px 1fr 180px 80px",
            },
          minWidth: "600px",
         }}
      >
        <Typography variant="body1" color="inherit" fontWeight={600}>Meepl Label</Typography>
        <Typography variant="body1" color="inherit" fontWeight={600}>Company Codes</Typography>
        <Typography variant="body1" color="inherit" fontWeight={600}>New Code</Typography>
        <Typography variant="body1" color="inherit" fontWeight={600}>Action</Typography>
      </Box>

      {/* Data Rows */}
      <Backdrop
        open={atdCodeLoading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(255, 255, 255, 0.5)",
          position: "absolute",
          borderRadius: "inherit",
        }}
      >
        <CircularProgress />
      </Backdrop>
      {rows?.map((row) => (
        <MappingRow
          key={row.attendance_bulk_notation_mapping_pk}
          row={row}
          handleAdd={handleAdd}
          handleDelete={handleDelete}
        />
      ))}
    </Box>
  );
};

// Single Row Component
const MappingRow = ({ row, handleAdd, handleDelete }) => {
  const [newCode, setNewCode] = useState("");

  return (
    <Box
      display="grid"
      alignItems="center"
      sx={{
        mb: 1,
        p: 1,
        borderRadius: 1,
        background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
         gridTemplateColumns: {
          xs: "150px 1fr 100px 80px",
          sm: "200px 1fr 180px 80px",
        },
        minWidth: "600px",
      }}
    >
      {/* Meepl Label */}
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          color: "inherit",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.meepl_label}
      </Typography>

      {/* Company Codes as Chips */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          alignItems: "center",
          minHeight: 40,
        }}
      >
        {row?.user_code?.length > 0 ? (
          row?.user_code?.map((code, idx) => {
          const isOnlyOne = row.user_code.length === 1;
           return (
            <Chip
                key={code?.attendance_bulk_notation_mapping_pk}
                label={code?.company_label}
                color="primary"
                variant="outlined"
                deleteIcon={
                  <Tooltip key={idx} title={isOnlyOne ? "At least one code is required" :"Delete this code"} arrow>
                    <DeleteIcon
                      fontSize="small"
                       onClick={(e) => {
                        e.stopPropagation();
                        if (!isOnlyOne) handleDelete(code);
                      }}
                      style={{
                        cursor: isOnlyOne ? "not-allowed" : "pointer",
                        color: isOnlyOne ? "#a5adbc" : "#637381",
                      }}
                    />
                  </Tooltip>
                }
                onDelete={() => {}}
                sx={{
                  cursor: "default",
                  "& .MuiChip-deleteIcon": {
                    marginLeft: "4px",
                    transition: "color 0.2s",
                    "&:hover": {
                      color: "#637381",
                    },
                  },
                }}
              />
            )
        })
        ) : (
          <Typography variant="body2" sx={{ color: "gray" }}>
            No codes yet
          </Typography>
        )}
      </Box>

      {/* Input Field */}
      <Box>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Type code"
          value={newCode}
          onChange={(e) => setNewCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd(row, newCode);
              setNewCode("");
            }
          }}
          sx={{width: "80%"}}
        />
      </Box>

      {/* Add Button */}
      <Box pl={1}>
        <Tooltip title="Add new company code">
          <IconButton
            color="primary"
            size="medium"
            onClick={() => {
              handleAdd(row, newCode);
              setNewCode("");
            }}
            disabled={!newCode.trim()}
          >
            <AddCircleIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default AttendanceCustomCodeGrid;
